import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, first, map, switchMap } from 'rxjs/operators';

import { GTMEventsCrash } from '../../shared/enums/Crashlytics';
import { AZURE_CA_URLS } from '../../shared/enums/HttpStatusCodes';
import { GAMIFICACION_ENDPOINTS } from '../../shared/models/Constants';
import { IRespListGames, IResptGetStatusLifes, IResptRating, IResptRatingByGame, IResptSaveGoing, IResptUserInfoGames, IUserInfoGames } from '../../shared/models/Games.model';
import { CrashlyticsService } from '../../shared/services/crashlytics/crashlytics.service';
import { SharedServiceService } from '../../shared/services/shared-service.service';
import {
    errorListGames,
    errorRating,
    errorRatingByGame,
    errorSaveGoing,
    errorSendConnection,
    errorSendStatusLifesModal,
    errorStartGame,
    errorStatusLifesModal,
    errorUserInfoGames,
    getListGames,
    getRating,
    getRatingByGame,
    getUserInfoGames,
    saveGoing,
    sendConnection,
    sendStatusLifesModal,
    startGame,
    statusLifesModal,
    successListGames,
    successRating,
    successRatingByGame,
    successSaveGoing,
    successSendConnection,
    successSendStatusLifesModal,
    successStartGame,
    successStatusLifesModal,
    successUserInfoGames
} from '../actions';
import { AppState } from '../app-reducers';

@Injectable()
export class GamesEffets {
    constructor(
        private actions$: Actions
        , private httpS: SharedServiceService
        , private crashlytics: CrashlyticsService
        , private store: Store<AppState>
    ) { }

    /**
     * Obtiene las notificaciones del usuario
     */
    public getListGames$ = createEffect(
        () => this.actions$.pipe(
            ofType(getListGames),
            switchMap(
                () => this.httpS.executeRequestAzureCA(GAMIFICACION_ENDPOINTS.get_list_games, null, this.httpS.getAzureCAUrl(AZURE_CA_URLS.GAMIFICACION))
                    .pipe(
                        map((resp: IRespListGames) => successListGames(resp)),
                        catchError((err: Error) => of(errorListGames({ payload: err })))
                    )
            )
        )
    );

    /**
     * Obtiene la info del usuario
     */
    public getUserInfoGames$ = createEffect(
        () => this.actions$.pipe(
            ofType(getUserInfoGames),
            switchMap(
                (actions) => this.httpS.executeRequestAzureCA(GAMIFICACION_ENDPOINTS.post_user_info_games, null, this.httpS.getAzureCAUrl(AZURE_CA_URLS.GAMIFICACION))
                    .pipe(
                        map((value: IResptUserInfoGames) => value.data),
                        concatMap<IUserInfoGames[], Observable<IUserInfoGames>>(resp => this.store.select('userData')
                            .pipe(
                                first(),
                                map(user => ({ ...resp[0], Responsable: user.Responsable, ID_Bodega: user.ID_Bodega, NUD: user.NUD }))
                            )
                        ),
                        map(data => successUserInfoGames(data)),
                        catchError((err: Error) => of(errorUserInfoGames({ payload: err })))
                    )
            )
        )
    );

    /**
     * Obtiene la info del leaderboard general
     */
    public getRating$ = createEffect(
        () => this.actions$.pipe(
            ofType(getRating),
            switchMap(
                (actions) => this.httpS.executeRequestAzureCA(GAMIFICACION_ENDPOINTS.post_leaderboard, null, this.httpS.getAzureCAUrl(AZURE_CA_URLS.GAMIFICACION))
                    .pipe(
                        map((resp: IResptRating) => successRating(resp)),
                        catchError((err: Error) => {
                            this.crashlytics.sendCrashPlatform(GTMEventsCrash.getRatingApi, err).then(() => { });
                            return of(errorRating({ payload: err }));
                        })
                    )
            )
        )
    );

    /**
     * Obtiene la info del leaderboard por juego
     */
    public getRatingByGame$ = createEffect(
        () => this.actions$.pipe(
            ofType(getRatingByGame),
            switchMap(
                (actions) => this.httpS.executeRequestAzureCA(GAMIFICACION_ENDPOINTS.post_leaderboard_by_game, { ...actions }, this.httpS.getAzureCAUrl(AZURE_CA_URLS.GAMIFICACION))
                    .pipe(
                        map((resp: IResptRatingByGame) => successRatingByGame(resp)),
                        catchError((err: Error) => {
                            this.crashlytics.sendCrashPlatform(GTMEventsCrash.getRatingByGameApi, err).then(() => { });
                            return of(errorRatingByGame({ payload: err }));
                        })
                    )
            )
        )
    );

    /**
     * Guarda la informacion de la partida
     */
    public saveGoing$ = createEffect(
        () => this.actions$.pipe(
            ofType(saveGoing),
            switchMap(
                (actions) => this.httpS.executeRequestAzureCA(GAMIFICACION_ENDPOINTS.post_add_game_history, { ...actions }, this.httpS.getAzureCAUrl(AZURE_CA_URLS.GAMIFICACION))
                    .pipe(
                        map((resp: IResptSaveGoing) => successSaveGoing(resp)),
                        catchError((err: Error) => of(errorSaveGoing({ payload: err })))
                    )
            )
        )
    );

    /**
     * Redime las vidas de la partida
     */
    public startGame$ = createEffect(
        () => this.actions$.pipe(
            ofType(startGame),
            switchMap(
                (actions) => this.httpS.executeRequestAzureCA(GAMIFICACION_ENDPOINTS.post_started_game, { ...actions }, this.httpS.getAzureCAUrl(AZURE_CA_URLS.GAMIFICACION))
                    .pipe(
                        map((resp: IResptSaveGoing) => successStartGame(resp)),
                        catchError((err: Error) => {
                            this.crashlytics.sendCrashPlatform(GTMEventsCrash.startGameApi, err).then(() => { });
                            return of(errorStartGame({ payload: err }));
                        })
                    )
            )
        )
    );

    /**
     * Consulta si es modal de aviso de seccion o modal de recompensa diaria
     */
    public statusLifesModal$ = createEffect(
        () => this.actions$.pipe(
            ofType(statusLifesModal),
            switchMap(
                (actions) => this.httpS.executeRequestAzureCA(GAMIFICACION_ENDPOINTS.post_status_lifes, null, this.httpS.getAzureCAUrl(AZURE_CA_URLS.GAMIFICACION))
                    .pipe(
                        map((resp: IResptGetStatusLifes) => successStatusLifesModal(resp)),
                        catchError((err: Error) => {
                            this.crashlytics.sendCrashPlatform(GTMEventsCrash.statusLifesApi, err).then(() => { });
                            return of(errorStatusLifesModal({ payload: err }));
                        })
                    )
            )
        )
    );

    /**
     * Consulta si es modal de aviso de seccion o modal de recompensa diaria
     */
    public sendStatusLifesModal$ = createEffect(
        () => this.actions$.pipe(
            ofType(sendStatusLifesModal),
            switchMap(
                (actions) => this.httpS.executeRequestAzureCA(GAMIFICACION_ENDPOINTS.post_set_status_lifes, null, this.httpS.getAzureCAUrl(AZURE_CA_URLS.GAMIFICACION))
                    .pipe(
                        map((resp: IResptSaveGoing) => successSendStatusLifesModal(resp)),
                        catchError((err: Error) => {
                            this.crashlytics.sendCrashPlatform(GTMEventsCrash.sendStatusLifesModalApi, err).then(() => { });
                            return of(errorSendStatusLifesModal({ payload: err }));
                        })
                    )
            )
        )
    );

    /**
     * Envia el evento de la info de dispositivo por conexion del app
     */
    public sendConnection$ = createEffect(
        () => this.actions$.pipe(
            ofType(sendConnection),
            switchMap(
                (actions) => this.httpS.executeRequestAzureCA(GAMIFICACION_ENDPOINTS.post_save_connection, actions, this.httpS.getAzureCAUrl(AZURE_CA_URLS.GAMIFICACION))
                    .pipe(
                        map((resp: IResptSaveGoing) => successSendConnection(resp)),
                        catchError((err: Error) => {
                            this.crashlytics.sendCrashPlatform(GTMEventsCrash.sendConnectionApi, err).then(() => { });
                            return of(errorSendConnection({ payload: err }));
                        })
                    )
            )
        )
    );
}
